import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Container, Heading, Text } from '../styles/components';
import MoogulIcon from './MoogulIcon';
import Tag from './Tag';
import CTAButton from './CTAButton';
import Modal from './Modal';
import ConvertkitFormComponent from './ConvertKitForm';
import BetaApplicationForm from './BetaApplicationForm';
import BetaApplicationResult from './BetaApplicationResult';
import { evaluateApplication } from '../utils/applicationScoring';
import ProcessingOverlay from './ProcessingOverlay';
import { submitToConvertKit } from '../utils/convertkit';
import { analyzeResponse } from '../utils/aiAnalysis';

const HeroSection = styled.section`
  height: 80svh;
  margin-top: -${props => props.theme.spacing.containerPadding};
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
`;

const HeroContainer = styled(Container)`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: ${props => props.theme.spacing.xl};
  align-items: center;
  max-width: ${props => props.theme.spacing.maxWidth};
  margin: 0 auto;
  padding: calc(${props => props.theme.spacing.containerPadding} * 2) 0 0 0;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.lg};
  }
`;

const HeroContent = styled.div`
  grid-column: span 7;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-column: 1 / -1;
  }
`;

const HeroImageWrapper = styled.div`
  grid-column: span 5;
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  min-height: 60svh;
  margin-left: -5svh;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-column: 1 / -1;
    justify-content: center;
    margin-left: 0;
    min-height: auto;
  }
`;

const HeroImage = styled.img`
  position: relative;
  left: 0;
  top: -6rem;
  z-index: 0;
  max-width: 45svw;
  max-height: 70svh;
  object-fit: contain;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    max-width: 100%;
  }
`;

const WahooText = styled.div`
  font-family: marvin, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 2.5rem;
  color: ${props => props.theme.colors.text};
  position: absolute;
  top: 4rem;
  left: -6rem;
  transform: rotate(-10deg);
  z-index: 2;
  text-shadow: 4px 4px 0 ${props => props.theme.colors.brand};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2rem;
    top: -30px;
    left: 0;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.75rem;
    top: -20px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    width: 100%;
  }
`;

const Hero = () => {
  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);
  const [isBetaModalOpen, setIsBetaModalOpen] = useState(false);
  const [applicationResult, setApplicationResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(null);

  const handleWaitlistClick = () => {
    setIsWaitlistModalOpen(true);
  };

  const handleBetaClick = () => {
    setIsBetaModalOpen(true);
  };

  const handleApplicationComplete = async (data) => {
    try {
      setIsProcessing(true);
      setError(null);
      setFormData(data);

      // Analyze the application using AI
      const result = await analyzeResponse(data);

      // Prepare data for ConvertKit
      const convertKitData = {
        ...data,
        applicationResult: result.result,
        applicationConfidence: result.confidence,
        applicationKeyFactors: result.metadata?.keyFactors?.join(', '),
        applicationRiskFactors: result.metadata?.riskFactors?.join(', '),
        applicationOnboardingFocus: result.metadata?.onboardingFocus?.join(', ')
      };

      // Submit to ConvertKit
      await submitToConvertKit(convertKitData);

      // Update UI with result
      setApplicationResult(result);
    } catch (err) {
      setError('There was an error processing your application. Please try again.');
      console.error('Application submission error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleModalClose = () => {
    setIsBetaModalOpen(false);
    setApplicationResult(null);
    setError(null);
    setFormData(null);
  };

  return (
    <HeroSection id="hero">
      <HeroContainer>
        <HeroContent>
          <Tag className="hero-tag">Strategy-first chat, content and brainstorming</Tag>
          <Heading as="h1" $mb="24px" $maxWidth="24ch">
            Your strategy is trapped.<br />
            Set it free with Moogul
          </Heading>
          <Text size="large">
            Gather your static docs, neglected notes and scattered brainwaves and put
            them into a single, dynamic growth hub with Moogul - the first ever Strategy
            Studio for small business.
          </Text>
          <ButtonGroup>
            <CTAButton onClick={handleWaitlistClick}>
              Join the waitlist and save $300
            </CTAButton>
            <Button $secondary onClick={handleBetaClick}>
              Get beta access now
            </Button>
          </ButtonGroup>
        </HeroContent>
        <HeroImageWrapper>
          <WahooText>WAHOO!</WahooText>
          <HeroImage 
            src="/assets/bird-and-cage.webp" 
            alt="A bird breaking free from its cage" 
          />
        </HeroImageWrapper>
      </HeroContainer>

      {/* Waitlist Modal */}
      <Modal 
        isOpen={isWaitlistModalOpen} 
        onClose={() => setIsWaitlistModalOpen(false)}
      >
        <ConvertkitFormComponent />
      </Modal>

      {/* Beta Application Modal */}
      <Modal 
        isOpen={isBetaModalOpen} 
        onClose={handleModalClose}
      >
        {isProcessing ? (
          <ProcessingOverlay />
        ) : error ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <Text color="error" $mb="1rem">{error}</Text>
            <Button onClick={() => setError(null)}>Try Again</Button>
          </div>
        ) : applicationResult ? (
          <BetaApplicationResult
            result={applicationResult.result}
            message={applicationResult.message}
            metadata={applicationResult.metadata}
            email={formData?.email}
            onClose={handleModalClose}
          />
        ) : (
          <BetaApplicationForm onComplete={handleApplicationComplete} />
        )}
      </Modal>
    </HeroSection>
  );
};

export default Hero; 